import React, { FC, useContext, useEffect, useMemo } from 'react';

import { useMutation } from '@apollo/client';
import {
  Button,
  H2,
  Input,
  Link,
  Modal,
  Paragraph,
  PasswordInput,
} from '@smartshell/ui-kit';
import { Controller, useForm } from 'react-hook-form';

import { LocalizationContext } from 'localization';
import notification from 'services/notificationService';
import {
  CONNECT_PAYMENT_SERVICES,
  DISCONNECT_PAYMENT_SERVICE,
  EConnectPaymentService,
  GET_SETTINGS_VALUE,
  IConnectPaymentServiceRequest,
  IDisconnectPaymentServiceRequest,
  SettingsContext,
} from 'settings';

import { IBaseModalProps } from '../interfaces';
import s from './BePaidModal.module.scss';
import { IBePaidFormData } from './interfaces';

export const BePaidModal: FC<IBaseModalProps> = ({ onClose }) => {
  const { t } = useContext(LocalizationContext);
  const {
    state: {
      settings: {
        bePaidEnabled,
        bePaidSecretKey,
        bePaidPublishableKey,
        bePaidShopId,
      },
    },
  } = useContext(SettingsContext);

  const {
    control,
    handleSubmit,
    formState: { isDirty },
    reset,
    watch,
  } = useForm<IBePaidFormData>({
    mode: 'onChange',
    defaultValues: {
      publishableKey: '',
      secretKey: '',
      shopId: '',
    },
  });

  const [connectPaymentService, { loading }] = useMutation<
    boolean,
    IConnectPaymentServiceRequest
  >(CONNECT_PAYMENT_SERVICES, {
    refetchQueries: [GET_SETTINGS_VALUE],
    awaitRefetchQueries: true,
  });

  const [
    disconnectPaymentService,
    { loading: disconnectPaymentServiceLoading },
  ] = useMutation<boolean, IDisconnectPaymentServiceRequest>(
    DISCONNECT_PAYMENT_SERVICE,
    {
      refetchQueries: [GET_SETTINGS_VALUE],
      awaitRefetchQueries: true,
    }
  );

  useEffect(() => {
    reset({
      secretKey: bePaidSecretKey || '',
      publishableKey: bePaidPublishableKey || '',
      shopId: bePaidShopId || '',
    });
  }, [bePaidPublishableKey, bePaidSecretKey, bePaidShopId]);

  const disconnectPaymentServiceHandler = (): void => {
    disconnectPaymentService({
      variables: { service: EConnectPaymentService.BEPAID },
    })
      .then(() =>
        notification.info({
          type: 'success',
          t,
          title: t('settings.integration.bePaid.disabled.title'),
          message: t('settings.integration.disabled.message'),
        })
      )
      .then(() => onClose())
      .catch((err) => console.error(err?.message));
  };

  const secretKey = watch('secretKey');
  const publishableKey = watch('publishableKey');
  const shopId = watch('shopId');
  const isDisabled = useMemo(
    () =>
      !isDirty ||
      secretKey.length < 24 ||
      publishableKey.length < 24 ||
      !shopId,
    [isDirty, secretKey, publishableKey, shopId]
  );

  return (
    <Modal onClose={onClose} className={s.container}>
      <div className={s.header}>
        <H2>{t('settings.integration.bePaid.modal.title')}</H2>
        <Paragraph>
          {t('settings.integration.stripe.modal.description')}
        </Paragraph>
      </div>
      <div className={s.body}>
        <form
          className={s.form}
          onSubmit={handleSubmit(({ secretKey, publishableKey, shopId }) => {
            if (!secretKey || !publishableKey || !shopId) return;

            connectPaymentService({
              variables: {
                service: EConnectPaymentService.BEPAID,
                input: {
                  bepaid_account: {
                    shop_id: shopId,
                    secret_key: secretKey,
                    publishable_key: publishableKey,
                  },
                },
              },
            })
              .then(() =>
                notification.info({
                  type: 'success',
                  t,
                  title: t('settings.integration.bePaid.modal.title'),
                  message: t('integration.online'),
                })
              )
              .then(() => onClose())
              .catch(() => {
                notification.info({
                  type: 'error',
                  t,
                  title: t('errors.error'),
                  message: t('settings.integration.bePaid.error.title'),
                });
              });
          })}
        >
          <Controller
            render={({ field, fieldState: { error } }) => (
              <Input
                {...field}
                title="Public key"
                variant="primary"
                error={error?.message}
              />
            )}
            name="publishableKey"
            control={control}
            rules={{
              required: t('errors.required'),
              minLength: {
                value: 32,
                message: t('errors.minLength', 32),
              },
            }}
          />
          <Controller
            render={({ field, fieldState: { error } }) => (
              <PasswordInput
                {...field}
                title="Secret key"
                error={error?.message}
                autoComplete="off"
              />
            )}
            name="secretKey"
            control={control}
            rules={{
              required: t('errors.required'),
              minLength: {
                value: 32,
                message: t('errors.minLength', 32),
              },
            }}
          />
          <Controller
            render={({ field, fieldState: { error } }) => (
              <Input
                {...field}
                title="Shop ID"
                variant="primary"
                error={error?.message}
              />
            )}
            name="shopId"
            control={control}
            rules={{
              required: t('errors.required'),
            }}
          />
          <Link
            title={t('settings.integration.bePaid.modal.api')}
            link="https://docs.bepaid.by/ru/using_api/id_key/"
          />
          <div className={s.buttons}>
            <Button
              fullWidth
              disabled={isDisabled || disconnectPaymentServiceLoading}
              type="submit"
              isLoading={loading}
            >
              {t('save')}
            </Button>
            <Button
              fullWidth
              variant="secondary"
              color="red"
              disabled={!bePaidEnabled || loading}
              isLoading={disconnectPaymentServiceLoading}
              onClick={disconnectPaymentServiceHandler}
            >
              {t('integration.offline')}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};
